<!--
  Placement location picker (card #336; always available since Jake + Mike,
  2026-10-21).

  Moves a SAVED placement to another location in place --
  PATCH /api/placements/<id>/ -- instead of delete + re-add. The design, its
  artwork for light/dark colors and its approvals stay.

  The size follows the location (Jake, 2026-10-27): a move takes the new
  location's default size (the size a new placement of the design gets
  there) and remembers the old one for the inspector's "switch back". When
  the store's client has not approved that size, the approval dialog asks
  first: keep the current size, or approve on the client's behalf
  (usePlacementSizeChange; one flow with the inspector's size inputs).

  The choices come from utils/placementLocationOptions (the Add-placement
  wizard's own source): the style's live views, or the zone catalog when the
  views are unknown; never a location another placement on this product
  holds. One component serves the inspector (always shown) and the rail
  (a placement the style no longer carries), so the two cannot drift.
-->
<template>
    <div class="placement-location">
        <!-- Sentinel: the location choices have loaded. -->
        <span v-if="ready" :data-testid="`${testid}-ready`" hidden></span>
        <select
            ref="selectEl"
            :data-testid="testid"
            class="placement-location-select"
            :aria-label="ariaLabel"
            :value="choices.current ?? ''"
            :disabled="moving"
            @change="onPicked($event.target.value)"
        >
            <option v-if="choices.current == null" value="" disabled>
                Choose a location...
            </option>
            <option
                v-for="opt in choices.options"
                :key="opt.value"
                :value="opt.value"
            >
                {{ opt.label }}
            </option>
        </select>
        <p
            v-if="error"
            :data-testid="`${testid}-error`"
            class="placement-location-error"
            role="alert"
        >
            {{ error }}
        </p>
        <PlacementSizeApprovalDialog
            :model-value="flow.dialog.open"
            :check="flow.dialog.check"
            :is-move="flow.dialog.isMove"
            :busy="flow.dialog.busy"
            :error="flow.dialog.error"
            @keep="flow.keep()"
            @proceed="flow.proceed()"
            @override="flow.override($event)"
            @cancel="flow.cancel()"
        />
    </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import api from "@/services/api";
import PlacementSizeApprovalDialog from "./PlacementSizeApprovalDialog.vue";
import { usePlacementSizeChange } from "./usePlacementSizeChange";
import { useProductEditorStore } from "@/stores/productEditor";
import { useProductEditorUiState } from "@/stores/productEditorUiState";
import {
    placementLocationChoices,
    skuViewsAuthority,
} from "@/utils/placementLocationOptions";

const props = defineProps({
    placement: { type: Object, required: true },
    testid: { type: String, required: true },
    ariaLabel: { type: String, default: "Placement location" },
});
const emit = defineEmits(["moved"]);

const editor = useProductEditorStore();
const editorUi = useProductEditorUiState();

const selectEl = ref(null);
const moving = ref(false);
const error = ref("");

// The zone catalog is the fail-open source, needed only while the style's
// views are unknown.
const zones = ref([]);
const zonesLoaded = ref(false);
let zonesRequested = false;

async function ensureZones() {
    if (zonesRequested) return;
    zonesRequested = true;
    try {
        const resp = await api.get("/placement_zones/");
        const body = (resp && resp.data) || {};
        zones.value = Array.isArray(body.data) ? body.data : [];
        zonesLoaded.value = true;
    } catch (_err) {
        // Allow a later attempt; the select simply has no choices meanwhile.
        zonesRequested = false;
        zones.value = [];
    }
}

const viewsKnown = computed(() => skuViewsAuthority(editorUi.skuViews) != null);

watch(
    viewsKnown,
    (known) => {
        if (!known) ensureZones();
    },
    { immediate: true },
);

const choices = computed(() =>
    placementLocationChoices({
        placement: props.placement,
        placements: editor.decorations ?? [],
        skuViews: editorUi.skuViews,
        zones: zones.value,
    }),
);

const ready = computed(() => viewsKnown.value || zonesLoaded.value);

// The inspector reuses this instance when another placement is selected; a
// refusal message belongs to the placement it was about.
watch(
    () => props.placement?.id,
    () => {
        error.value = "";
    },
);

// Reload the editor's server state; true when the reload landed.
async function refresh() {
    if (typeof editor.loadServerState !== "function") return false;
    try {
        await editor.loadServerState({ force: true, background: true });
        return true;
    } catch (_err) {
        return false;
    }
}

// The flow's reload after a write; remembers whether it landed.
let refreshed = true;
const flow = usePlacementSizeChange({
    refresh: async () => {
        refreshed = await refresh();
        return refreshed;
    },
});

function labelOf(value) {
    const opt = (choices.value.options || []).find((o) => o.value === value);
    return opt ? opt.label : "";
}

async function onPicked(value) {
    const { mode, current } = choices.value;
    if (!value || value === current) return;
    const location =
        mode === "view"
            ? { view_code: value }
            : { placement_zone_id: Number(value) };
    moving.value = true;
    error.value = "";
    refreshed = true;
    try {
        const result = await flow.apply({
            placementId: props.placement.id,
            location,
            fromLabel: labelOf(current),
        });
        if (result.status === "cancelled") {
            await putBack(current);
            return;
        }
        if (result.status === "error") {
            error.value = result.error;
            await putBack(current);
            // Another placement took that location meanwhile: reload so the
            // choices are honest.
            if (result.httpStatus === 409) await refresh();
            return;
        }
        if (result.warning) error.value = result.warning;
        if (!refreshed) {
            // Saved, but the editor still shows the old location; rendering
            // now would draw the stale one. Say so instead.
            error.value =
                "The location was changed, but this page couldn't refresh. "
                + "Reload the page to see it.";
            return;
        }
        // The move is not a product save, so tell the decorated-render
        // triggers -- once the reload has the placement at its new location
        // -- exactly as the Add-placement wizard's create does (#356).
        if (typeof editor.notePlacementSaved === "function") {
            editor.notePlacementSaved();
        }
        emit("moved");
    } finally {
        moving.value = false;
    }
}

// Show the current location again (a cancelled or refused move).
async function putBack(current) {
    await nextTick();
    if (selectEl.value) selectEl.value.value = current ?? "";
}
</script>

<style scoped>
.placement-location-select {
    width: 100%;
    font-size: 12px;
    padding: 4px 6px;
    border: 1px solid var(--tf-border-default, #d1d5db);
    border-radius: 4px;
    background: var(--tf-bg-surface, #fff);
}

.placement-location-error {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--tf-color-danger, #b91c1c);
}
</style>
